import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { stringifyIfNotNullOrUndefined } from '../../../../../helper/helper';
import { DoctorEntity } from '../../../../entities/mdqx/doctor/doctor.entity';
import { HttpService } from '../../../../services/http.service';
import { BaseActionTypes } from '../../../actions/base.actions';
import { DeleteDoctorResponse, DoctorActions, DoctorActionTypes, GetDoctorsResponse, PatchDoctorResponse, PostDoctorResponse } from '../../../actions/mdqx/doctor/doctor.actions';
import { State } from '../../../index';
import { getToken } from '../../../selectors/base.selectors';

enum ErrorCodes {
    Get = 'Ärzte Abrufen fehlgeschlagen',
    Post = 'Arzt Anlegen fehlgeschlagen',
    Patch = 'Arzt Bearbeiten fehlgeschlagen',
    Delete = 'Arzt Löschen fehlgeschlagen',
}

@Injectable()
export class DoctorEffects {
    getDoctors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DoctorActionTypes.GetDoctorsRequest),
            map(
                () => `
            {
                doctor {
                    ${DoctorEntity.GqlFields.join(' ')}
                }
            }`,
            ),
            withLatestFrom(this.store.select(getToken)),
            switchMap(([query, token]) =>
                this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (!res?.doctor) {
                            throw 'broken response';
                        }
                        return new GetDoctorsResponse(res.doctor.map((doctor) => new DoctorEntity(doctor)));
                    }),
                    catchError((err, caught) => {
                        return of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Get,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        );
                    }),
                ),
            ),
        ),
    );

    postDoctor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DoctorActionTypes.PostDoctorRequest),
            map(
                ({ Payload }) => `
            mutation {
                createDoctor(
                    customerIds: ${JSON.stringify(Payload.customerIds)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'isActive')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'lanr')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'name')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'schwerpunkt')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'qualifikation')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'email')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'qualificationIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'membershipIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'typeoftreatmentsIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'doctorCatalogIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'serviceGroupIds')}
                ) {
                    ${DoctorEntity.GqlFields.join(' ')}
                }
            }`,
            ),
            withLatestFrom(this.store.select(getToken)),
            switchMap(([query, token]) =>
                this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (!res?.createDoctor) {
                            throw 'broken response';
                        }
                        return new PostDoctorResponse(new DoctorEntity(res.createDoctor));
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Post,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    patchDoctor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DoctorActionTypes.PatchDoctorRequest),
            map(
                ({ Payload }) => `
            mutation {
                changeDoctor(
                    id: ${JSON.stringify(Payload.id)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'isActive')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'lanr')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'name')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'schwerpunkt')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'email')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'qualificationIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'membershipIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'typeoftreatmentsIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'doctorCatalogIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerIds')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'serviceGroupIds')}
                ) {
                    ${DoctorEntity.GqlFields.join(' ')}
                }
            }`,
            ),
            withLatestFrom(this.store.select(getToken)),
            switchMap(([query, token]) =>
                this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (!res?.changeDoctor) {
                            throw 'broken response';
                        }
                        return new PatchDoctorResponse(new DoctorEntity(res.changeDoctor));
                    }),
                    catchError((err, caught) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Patch,
                                    Err: err,
                                    Caught: caught,
                                },
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteDoctor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DoctorActionTypes.DeleteDoctorRequest),
            withLatestFrom(this.store.select(getToken)),
            switchMap(([{ Payload }, token]) =>
                this.api.graphQl(
                        {
                            query: `
                        mutation {
                            deleteDoctor(id: ${JSON.stringify(Payload.id)})
                        }`,
                        },
                        { token },
                        '',
                        environment.mdqxGatewayApiUrl,
                    ).pipe(
                        map((res) => {
                            if (!res?.deleteDoctor) {
                                throw 'broken response';
                            }
                            return new DeleteDoctorResponse(Payload.id);
                        }),
                        catchError((err, caught) =>
                            of(
                                BaseActionTypes.ErrorAction({
                                    Payload: {
                                        ToasterMessage: ErrorCodes.Delete,
                                        Err: err,
                                        Caught: caught,
                                    },
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    constructor(private actions$: Actions<DoctorActions>, private api: HttpService, private store: Store<State>) {}
}
